import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Stack
} from '@mui/material';
import {
  FaPen,
  FaCalendarAlt,
  FaEnvelope,
  FaFont,
  FaCheck,
  FaExclamation,
  FaLock,
  FaCheckCircle
} from 'react-icons/fa';
import { MdClose, MdGesture, MdTextFields, MdCheckBox } from 'react-icons/md';
import SignatureCanvas from 'react-signature-canvas';
import { AssignedDocument, InputField } from '../types';

interface InputFieldOverlayProps {
  field: InputField;
  selectedDocument?: AssignedDocument;
  pdfContainerRef: React.RefObject<HTMLDivElement>;
  handleFieldClick: (field: InputField) => void;
  updateFieldValue?: (fieldId: string, value: string) => void;
  isMobile?: boolean;
}

const AssignedInputFieldOverlay: React.FC<InputFieldOverlayProps> = ({
  field,
  selectedDocument,
  pdfContainerRef,
  handleFieldClick,
  updateFieldValue,
  isMobile = false
}) => {
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signatureMode, setSignatureMode] = useState<'pad' | 'name' | 'standard'>('pad');
  const [typedName, setTypedName] = useState<string>('');
  const [isStandardChecked, setIsStandardChecked] = useState<boolean>(false);
  const signatureCanvasRef = React.useRef<SignatureCanvas>(null);

  if (!pdfContainerRef.current) return null;

  // Check for either canvas (PDF) or img (image) element
  const documentElement = pdfContainerRef.current.querySelector('canvas, img');
  if (!documentElement) return null;

  const actualDocWidth = documentElement.clientWidth;
  const actualDocHeight = documentElement.clientHeight;
  if (actualDocWidth === 0 || actualDocHeight === 0) return null; // Skip render if dimensions not ready

  const isImageDocument = documentElement.tagName.toLowerCase() === 'img';

  // Calculate position and size
  let left: number, top: number, width: number, height: number;

  if (field.xPercent !== undefined && field.yPercent !== undefined) {
    left = (field.xPercent / 100) * actualDocWidth;
    top = (field.yPercent / 100) * actualDocHeight;
    if (field.widthPercent !== undefined && field.heightPercent !== undefined) {
      width = (field.widthPercent / 100) * actualDocWidth;
      height = (field.heightPercent / 100) * actualDocHeight;
    } else {
      const defaultWidth = field.type === 'signature' ? 0.18 : 0.12;
      const defaultHeight = field.type === 'signature' ? 0.05 : 0.03;
      width = defaultWidth * actualDocWidth;
      height = defaultHeight * actualDocHeight;
    }
  } else {
    if (isImageDocument) {
      // For images, use direct pixel positioning
      left = field.x || 0;
      top = field.y || 0;
      const defaultWidth = field.type === 'signature' ? 120 : 80;
      const defaultHeight = field.type === 'signature' ? 35 : 22;
      width = field.width || defaultWidth;
      height = field.height || defaultHeight;
    } else {
      // For PDFs, use scaling based on A4 dimensions
      const basePdfWidth = 595; // Standard A4 width in points
      const basePdfHeight = 842; // Standard A4 height in points
      const scaleX = actualDocWidth / basePdfWidth;
      const scaleY = actualDocHeight / basePdfHeight;
      left = (field.x || 0) * scaleX;
      top = (field.y || 0) * scaleY;
      const defaultWidth = field.type === 'signature' ? 120 : 80;
      const defaultHeight = field.type === 'signature' ? 35 : 22;
      width = (field.width || defaultWidth) * scaleX;
      height = (field.height || defaultHeight) * scaleY;
    }
  }

  // Ensure minimum dimensions and bounds
  width = Math.max(width, 80);
  height = Math.max(height, 24);
  left = Math.max(0, Math.min(left, actualDocWidth - width));
  top = Math.max(0, Math.min(top, actualDocHeight - height));

  const getFieldColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'signature':
        return '#4f46e5';
      case 'text':
      case 'name':
      case 'title':
        return '#374151';
      case 'date':
      case 'expiry':
        return '#6b7280';
      case 'email':
        return '#4b5563';
      default:
        return '#6b7280';
    }
  };

  const fieldColor = getFieldColor(field.type);
  const isEditable = field.isEmpEdit && !selectedDocument?.isApproved;
  const hasValue = Boolean(field.value && field.value.toString().trim() !== '');
  const isRequiredAndEmpty = field.isRequired && field.isEmpEdit && !hasValue;
  const isSignatureField = field.type.toLowerCase() === 'signature';

  const getTooltipText = () => {
    const parts = [field.fieldLabel || field.type];
    if (field.isRequired) parts.push('• Required');
    if (isRequiredAndEmpty) parts.push('• Needs attention');
    parts.push(isEditable ? '• Click to edit' : '• Read only');
    return parts.join(' ');
  };

  const handleSignatureFieldClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEditable && isSignatureField) {
      setShowSignatureDialog(true);
      if (field.value) {
        if (field.value === 'checked') {
          setSignatureMode('standard');
          setIsStandardChecked(true);
        } else if (typeof field.value === 'string' && !field.value.startsWith('data:image')) {
          setSignatureMode('name');
          setTypedName(field.value);
        } else {
          setSignatureMode('pad');
        }
      }
    } else if (isEditable) {
      handleFieldClick(field);
    }
  };

  const handleSignatureSave = () => {
    if (!updateFieldValue) {
      console.warn('updateFieldValue function not provided');
      return;
    }

    let signatureValue = '';
    switch (signatureMode) {
      case 'pad':
        if (signatureCanvasRef.current && !signatureCanvasRef.current.isEmpty()) {
          signatureValue = signatureCanvasRef.current.toDataURL();
        }
        break;
      case 'name':
        signatureValue = typedName.trim();
        break;
      case 'standard':
        signatureValue = isStandardChecked ? 'checked' : '';
        break;
    }

    if (signatureValue) {
      updateFieldValue(field.id, signatureValue);
      setShowSignatureDialog(false);
      setTypedName('');
      setIsStandardChecked(false);
      if (signatureCanvasRef.current) {
        signatureCanvasRef.current.clear();
      }
    }
  };

  const handleSignatureClose = () => {
    setShowSignatureDialog(false);
    setTypedName('');
    setIsStandardChecked(false);
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.clear();
    }
  };

  return (
    <>
      <Tooltip key={field.id} title={getTooltipText()} placement="top" arrow>
        <Box
          sx={{
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isEditable ? 'pointer' : 'default',
            backgroundColor: isEditable
              ? hasValue
                ? 'rgba(255, 255, 255, 0.98)'
                : 'rgba(249, 250, 251, 0.95)'
              : 'rgba(243, 244, 246, 0.9)',
            border: `1px solid ${
              isRequiredAndEmpty ? '#ef4444' : isEditable ? '#d1d5db' : '#e5e7eb'
            }`,
            borderRadius: '4px',
            transition: 'all 0.2s ease-out',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '&:hover': isEditable
              ? {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  borderColor: fieldColor,
                  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                  transform: 'translateY(-0.5px)'
                }
              : {},
            zIndex: isImageDocument ? 100000 : 1000, // Higher z-index for images
            overflow: 'visible',
            padding: '2px 8px'
          }}
          onClick={handleSignatureFieldClick}
        >
          {isSignatureField ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.5,
                overflow: 'hidden'
              }}
            >
              {hasValue ? (
                typeof field.value === 'string' && field.value.startsWith('data:image') ? (
                  <img
                    src={String(field.value)}
                    alt="Signature"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: '2px'
                    }}
                  />
                ) : field.value === 'checked' ? (
                  <FaCheckCircle style={{ color: '#10b981', fontSize: '12px' }} />
                ) : (
                  <Typography
                    sx={{
                      fontFamily: '"Brush Script MT", cursive, "Dancing Script", cursive',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: '#374151',
                      lineHeight: 1.1,
                      letterSpacing: '0.3px'
                    }}
                  >
                    {String(field.value)}
                  </Typography>
                )
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: '#9ca3af'
                  }}
                >
                  <FaPen style={{ fontSize: '9px' }} />
                  <Typography
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 500,
                      letterSpacing: '0.025em'
                    }}
                  >
                    Sign
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: hasValue ? 500 : 400,
                  color: hasValue ? '#374151' : '#9ca3af',
                  fontStyle: hasValue ? 'normal' : 'italic',
                  lineHeight: 1.2,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  width: '100%',
                  letterSpacing: '0.015em',
                  fontFamily:
                    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
              >
                {hasValue ? String(field.value) : 'Click to edit...'}
              </Typography>
            </Box>
          )}

          {hasValue && isSignatureField && (
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                bgcolor: '#10b981',
                borderRadius: '50%',
                width: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                zIndex: isImageDocument ? 100001 : 1002
              }}
            >
              <FaCheck style={{ color: 'white', fontSize: '8px' }} />
            </Box>
          )}

          {isRequiredAndEmpty && (
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                bgcolor: '#ef4444',
                borderRadius: '50%',
                width: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                animation: 'pulse 1.5s infinite',
                zIndex: isImageDocument ? 100001 : 1002
              }}
            >
              <FaExclamation style={{ color: 'white', fontSize: '8px' }} />
            </Box>
          )}

          {!isEditable && hasValue && (
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                left: -8,
                bgcolor: '#6b7280',
                borderRadius: '50%',
                width: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                zIndex: isImageDocument ? 100001 : 1002
              }}
            >
              <FaLock style={{ color: 'white', fontSize: '8px' }} />
            </Box>
          )}

          <Box
            sx={{
              position: 'absolute',
              bottom: -8,
              left: -8,
              bgcolor: 'white',
              borderRadius: '50%',
              width: 18,
              height: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #e5e7eb',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              zIndex: isImageDocument ? 100001 : 1002
            }}
          >
            {field.type === 'signature' && <FaPen style={{ color: '#4f46e5', fontSize: '8px' }} />}
            {(field.type === 'date' || field.type === 'expiry') && (
              <FaCalendarAlt style={{ color: '#6b7280', fontSize: '8px' }} />
            )}
            {field.type === 'email' && <FaEnvelope style={{ color: '#4b5563', fontSize: '8px' }} />}
            {(field.type === 'text' || field.type === 'name' || field.type === 'title') && (
              <FaFont style={{ color: '#374151', fontSize: '8px' }} />
            )}
          </Box>
        </Box>
      </Tooltip>

      <Dialog
        open={showSignatureDialog}
        onClose={handleSignatureClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1.5,
            minHeight: signatureMode === 'pad' ? (isMobile ? 320 : 380) : isMobile ? 220 : 280
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            fontSize: isMobile ? '1rem' : '1.1rem'
          }}
        >
          Add Signature
          <IconButton onClick={handleSignatureClose} size="small" sx={{ color: 'text.secondary' }}>
            <MdClose size={16} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Tabs
            value={signatureMode}
            onChange={(_, newValue) => setSignatureMode(newValue)}
            variant="fullWidth"
            sx={{
              mb: 2,
              '& .MuiTab-root': {
                minHeight: 32,
                fontSize: '0.75rem',
                textTransform: 'none',
                fontWeight: 500,
                px: 1
              },
              '& .MuiTabs-indicator': { height: 2 }
            }}
          >
            <Tab icon={<MdGesture size={14} />} label="Draw" value="pad" iconPosition="start" />
            <Tab icon={<MdTextFields size={14} />} label="Type" value="name" iconPosition="start" />
            <Tab
              icon={<MdCheckBox size={14} />}
              label="Check"
              value="standard"
              iconPosition="start"
            />
          </Tabs>

          <Box
            sx={{
              minHeight: isMobile ? 140 : 180,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {signatureMode === 'pad' && (
              <Paper
                elevation={2}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid rgba(79, 70, 229, 0.2)'
                }}
              >
                <SignatureCanvas
                  ref={signatureCanvasRef}
                  canvasProps={{
                    width: isMobile ? 260 : 340,
                    height: isMobile ? 100 : 140,
                    style: { backgroundColor: '#fff', borderRadius: '4px', cursor: 'crosshair' }
                  }}
                  penColor="black"
                  minWidth={1}
                  maxWidth={2.5}
                />
              </Paper>
            )}

            {signatureMode === 'name' && (
              <Box sx={{ width: '100%', maxWidth: 320 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Your Full Name"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Enter your full name"
                  variant="outlined"
                  sx={{
                    mb: 2,
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      textAlign: 'center',
                      fontFamily: 'cursive'
                    }
                  }}
                />
                {typedName && (
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: '#f8f9fa',
                      border: '1px solid rgba(79, 70, 229, 0.2)',
                      textAlign: 'center'
                    }}
                  >
                    <Typography
                      sx={{ fontFamily: 'cursive', color: 'text.primary', fontSize: '1.1rem' }}
                    >
                      {typedName}
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {signatureMode === 'standard' && (
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  bgcolor: '#f8f9fa',
                  border: '1px solid rgba(79, 70, 229, 0.2)',
                  minWidth: 160
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isStandardChecked}
                      onChange={(e) => setIsStandardChecked(e.target.checked)}
                      sx={{
                        transform: 'scale(1.5)',
                        color: 'success.main',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ ml: 1, fontSize: '0.9rem' }}>Standard Checkmark</Typography>
                  }
                />
              </Paper>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              if (signatureMode === 'pad' && signatureCanvasRef.current) {
                signatureCanvasRef.current.clear();
              } else if (signatureMode === 'name') {
                setTypedName('');
              } else {
                setIsStandardChecked(false);
              }
            }}
            color="secondary"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          >
            Clear
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={handleSignatureClose}
              color="primary"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSignatureSave}
              color="primary"
              size="small"
              variant="contained"
              disabled={
                (signatureMode === 'pad' && signatureCanvasRef.current?.isEmpty()) ||
                (signatureMode === 'name' && !typedName.trim()) ||
                (signatureMode === 'standard' && !isStandardChecked)
              }
              sx={{ fontSize: '0.75rem' }}
            >
              Save
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssignedInputFieldOverlay;
